import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import Export360PdfButtons from "@/components/Export360PdfButtons";
import { Users, Lock, ArrowLeft, TrendingUp, AlertCircle, CheckCircle2, Sparkles, RefreshCw, Share2 } from "lucide-react";

const COHORT_META = {
  manager: { label: "Manager", color: "#111", bar: "#111", short: "M" },
  peer: { label: "Peer avg", color: "#FF4F00", bar: "#FF4F00", short: "P" },
  direct_report: { label: "DR avg", color: "#00A36C", bar: "#00A36C", short: "DR" },
  self: { label: "Self", color: "#6B5BD2", bar: "#6B5BD2", short: "S" },
};

const SCORE_BAND = (s) => {
  if (s == null) return { label: "—", tone: "text-[#999]" };
  if (s < 2) return { label: "Developing", tone: "text-[#B00020]" };
  if (s < 3) return { label: "Progressing", tone: "text-[#D97706]" };
  if (s < 4) return { label: "Meeting", tone: "text-[#00A36C]" };
  if (s < 4.5) return { label: "Exceeding", tone: "text-[#00A36C]" };
  return { label: "Role-model", tone: "text-[#111]" };
};

function CohortBar({ cohort, value, max = 5 }) {
  const meta = COHORT_META[cohort];
  const pct = value == null ? 0 : Math.max(2, Math.min(100, (value / max) * 100));
  const empty = value == null;
  return (
    <div className="flex items-center gap-3" data-testid={`cohort-bar-${cohort}`}>
      <div className="w-20 shrink-0">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">{meta.label}</div>
      </div>
      <div className="flex-1 h-6 bg-[#F4F4F0] border border-[#111] relative">
        {!empty && (
          <div
            className="h-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: meta.bar }}
          />
        )}
        {empty && (
          <div className="absolute inset-0 flex items-center justify-center font-mono-cal text-[9px] uppercase text-[#999] tracking-widest">
            no data
          </div>
        )}
      </div>
      <div className="w-16 text-right">
        <span className="font-display font-bold text-base" data-testid={`cohort-value-${cohort}`}>
          {value == null ? "—" : value.toFixed(2)}
        </span>
        <span className="font-mono-cal text-[10px] text-[#999]">/5</span>
      </div>
    </div>
  );
}

function CommentsPanel({ title, comments, testid }) {
  const empty = (comments || []).length === 0;
  return (
    <div className="cal-card p-4" data-testid={testid}>
      <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">{title}</div>
      {empty ? (
        <div className="text-sm text-[#999] italic">No comments submitted.</div>
      ) : (
        <ul className="space-y-2">
          {comments.map((c, i) => (
            <li key={i} className="text-sm text-[#333] border-l-2 border-[#111]/20 pl-3 py-1" data-testid={`${testid}-item-${i}`}>
              <span className="text-[#111]">"{c}"</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Results360() {
  const { cycleId } = useParams();
  const nav = useNavigate();
  const [cycle, setCycle] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Phase 6: AI Peer Consensus synthesis
  const [synthesis, setSynthesis] = useState(null);
  const [synthesisAt, setSynthesisAt] = useState(null);
  const [synthesizing, setSynthesizing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cRes, rRes, sRes] = await Promise.all([
          api.get(`/review-cycles/${cycleId}`),
          api.get(`/review-cycles/${cycleId}/results`),
          api.get(`/review-cycles/${cycleId}/synthesis`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setCycle(cRes.data);
        setResults(rRes.data);
        setSynthesis(sRes.data?.synthesis || null);
        setSynthesisAt(sRes.data?.generated_at || null);
      } catch (err) {
        if (!cancelled) {
          setError(apiError(err, "Could not load 360 results"));
          toast.error(apiError(err, "Could not load 360 results"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cycleId]);

  const runSynthesis = async () => {
    setSynthesizing(true);
    try {
      const r = await api.post(`/review-cycles/${cycleId}/synthesize`);
      setSynthesis(r.data?.synthesis || null);
      setSynthesisAt(r.data?.generated_at || null);
      toast.success("AI consensus ready");
    } catch (err) {
      toast.error(apiError(err, "Synthesis failed"));
    } finally {
      setSynthesizing(false);
    }
  };

  const weightedOverall = useMemo(() => {
    if (!results?.unlocked || !results.per_task?.length) return null;
    const vals = results.per_task.map((t) => t.overall).filter((v) => v != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [results]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-6 py-16 font-mono-cal text-xs" data-testid="results-loading">[ LOADING 360 RESULTS… ]</div>;
  }
  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16" data-testid="results-error">
        <div className="cal-card p-8 text-center">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#B00020] mb-2">[ ERROR ]</div>
          <h2 className="font-display font-bold text-2xl mb-2">Can't load results</h2>
          <p className="text-sm text-[#666] mb-4">{error}</p>
          <button onClick={() => nav(-1)} className="cal-btn">← Back</button>
        </div>
      </div>
    );
  }

  const profile = cycle?.profile_snapshot || {};
  const unlocked = !!results?.unlocked;
  const weights = results?.weights || cycle?.weights || { manager: 0.5, peers: 0.25, direct_reports: 0.25 };
  const counts = results?.cohort_counts || {};

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="results-360-page">
      {/* Back nav + share action */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <button
          onClick={() => nav(`/profiles/${cycle?.profile_id}`)}
          className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] hover:text-[#111] inline-flex items-center gap-1"
          data-testid="back-to-profile"
        >
          <ArrowLeft size={12} /> Back to profile
        </button>
        {unlocked && (
          <div className="flex flex-wrap items-center gap-2">
            <Export360PdfButtons cycleId={cycle?.id} employeeName={profile.employee_name} />
            <ShareWithEmployeeButton
              cycle={cycle}
              onShared={(payload) => setCycle({ ...cycle, employee_share_shared_at: payload.shared_at, employee_share_token: payload.share_url?.split("/").pop() })}
            />
          </div>
        )}
      </div>

      {/* Header */}
      <div className="cal-card cal-shadow p-6 sm:p-8 mb-8" data-testid="results-header">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2 flex items-center gap-2">
          <Users size={12} /> [ 360° RESULTS · COHORT AGGREGATED ]
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl mb-1" data-testid="results-employee-name">
          {profile.employee_name || "Employee"}
        </h1>
        <div className="text-sm text-[#666] mb-4">
          <span className="font-mono-cal text-xs">{profile.employee_title}</span>
          {profile.department && <span className="font-mono-cal text-xs"> · {profile.department}</span>}
          <span className="font-mono-cal text-xs"> · {cycle?.period_label || cycle?.period}</span>
        </div>

        {unlocked ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="border border-[#111] p-4 bg-[#FAFAF5]" data-testid="overall-score-card">
              <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666] mb-1">[ WEIGHTED OVERALL ]</div>
              <div className="flex items-baseline gap-1">
                <span className="font-display font-bold text-5xl" data-testid="overall-score">
                  {weightedOverall != null ? weightedOverall.toFixed(2) : "—"}
                </span>
                <span className="font-mono-cal text-sm text-[#666]">/5</span>
              </div>
              <div className={`font-mono-cal text-[10px] uppercase tracking-widest mt-1 ${SCORE_BAND(weightedOverall).tone}`}>
                {SCORE_BAND(weightedOverall).label}
              </div>
            </div>
            <div className="border border-[#111] p-4" data-testid="submissions-card">
              <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666] mb-2">[ SUBMISSIONS ]</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Peers</span><span className="font-mono-cal font-bold" data-testid="count-peer">{counts.peer || 0}</span></div>
                {(counts.direct_report || 0) > 0 && (
                  <div className="flex justify-between"><span>Direct reports</span><span className="font-mono-cal font-bold" data-testid="count-dr">{counts.direct_report}</span></div>
                )}
                <div className="flex justify-between"><span>Manager</span><span className="font-mono-cal font-bold text-[#00A36C]" data-testid="count-manager">✓</span></div>
                <div className="flex justify-between"><span>Self</span><span className="font-mono-cal font-bold text-[#00A36C]" data-testid="count-self">✓</span></div>
              </div>
            </div>
            <div className="border border-[#111] p-4" data-testid="weights-card">
              <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666] mb-2">[ COHORT WEIGHTS ]</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Manager</span><span className="font-mono-cal font-bold">{Math.round((weights.manager || 0) * 100)}%</span></div>
                <div className="flex justify-between"><span>Peers</span><span className="font-mono-cal font-bold">{Math.round((weights.peers || 0) * 100)}%</span></div>
                <div className="flex justify-between"><span>Direct reports</span><span className="font-mono-cal font-bold">{Math.round((weights.direct_reports || 0) * 100)}%</span></div>
              </div>
            </div>
          </div>
        ) : (
          <LockedState results={results} />
        )}
      </div>

      {/* AI Peer Consensus (Phase 6) — only visible when unlocked */}
      {unlocked && (
        <AiConsensusPanel
          synthesis={synthesis}
          generatedAt={synthesisAt}
          loading={synthesizing}
          onGenerate={runSynthesis}
          perTask={results.per_task || []}
        />
      )}

      {/* Per-task cohort bars */}
      {unlocked && results.per_task && results.per_task.length > 0 && (
        <section className="mb-8" data-testid="per-task-section">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} />
            <h2 className="font-display font-bold text-2xl">Task-level breakdown</h2>
          </div>
          <div className="space-y-5">
            {results.per_task.map((t, i) => (
              <div key={t.task_id} className="cal-card p-5" data-testid={`task-row-${t.task_id}`}>
                <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">[ TASK {i + 1} ]</div>
                    <div className="font-display font-bold text-base" data-testid={`task-desc-${t.task_id}`}>
                      {t.description}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666]">[ OVERALL ]</div>
                    <div className="font-display font-bold text-2xl" data-testid={`task-overall-${t.task_id}`}>
                      {t.overall != null ? t.overall.toFixed(2) : "—"}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <CohortBar cohort="peer" value={t.peer?.avg} />
                  {(t.direct_report?.n || 0) > 0 && <CohortBar cohort="direct_report" value={t.direct_report?.avg} />}
                  <CohortBar cohort="manager" value={t.manager?.score} />
                  <CohortBar cohort="self" value={t.self?.score} />
                </div>
                {/* Task comments (combined, anonymised) */}
                {(() => {
                  const allComments = [
                    ...(t.peer?.comments || []).map((c) => ({ cohort: "peer", text: c })),
                    ...(t.direct_report?.comments || []).map((c) => ({ cohort: "direct_report", text: c })),
                    ...(t.manager?.comments || []).map((c) => ({ cohort: "manager", text: c })),
                    ...(t.self?.comments || []).map((c) => ({ cohort: "self", text: c })),
                  ];
                  if (!allComments.length) return null;
                  return (
                    <details className="mt-3" data-testid={`task-comments-${t.task_id}`}>
                      <summary className="cursor-pointer font-mono-cal text-[10px] uppercase tracking-widest text-[#666] hover:text-[#111]">
                        Task comments ({allComments.length})
                      </summary>
                      <table className="w-full mt-3 text-sm">
                        <tbody>
                          {allComments.map((c, j) => (
                            <tr key={j} className="border-t border-[#111]/10" data-testid={`task-comment-row-${t.task_id}-${j}`}>
                              <td className="py-2 pr-3 w-24 align-top">
                                <span className="font-mono-cal text-[9px] uppercase tracking-widest" style={{ color: COHORT_META[c.cohort].color }}>
                                  [{COHORT_META[c.cohort].short}]
                                </span>
                              </td>
                              <td className="py-2 text-[#333]">"{c.text}"</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  );
                })()}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Cohort-grouped overall comments */}
      {unlocked && results.cohort_comments && (
        <section data-testid="cohort-comments-section">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="font-display font-bold text-2xl">Written feedback</h2>
            <span className="font-mono-cal text-[10px] text-[#999] uppercase tracking-widest">[ ANONYMISED · GROUPED BY COHORT ]</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(["peer", "direct_report", "manager", "self"]).map((cohort) => {
              const bucket = results.cohort_comments[cohort];
              if (!bucket) return null;
              const total = (bucket.strengths?.length || 0) + (bucket.growth_areas?.length || 0) + (bucket.overall?.length || 0);
              if (total === 0 && cohort !== "manager" && cohort !== "self") return null;
              return (
                <div key={cohort} className="space-y-3" data-testid={`cohort-group-${cohort}`}>
                  <div className="font-mono-cal text-[11px] uppercase tracking-widest border-b border-[#111] pb-1" style={{ color: COHORT_META[cohort].color }}>
                    [ {COHORT_META[cohort].label.toUpperCase()} ] {counts[cohort] ? `· ${counts[cohort]} submission${counts[cohort] > 1 ? "s" : ""}` : ""}
                  </div>
                  <CommentsPanel title="Strengths" comments={bucket.strengths} testid={`comments-${cohort}-strengths`} />
                  <CommentsPanel title="Growth areas" comments={bucket.growth_areas} testid={`comments-${cohort}-growth`} />
                  <CommentsPanel title="Overall notes" comments={bucket.overall} testid={`comments-${cohort}-overall`} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Footer anonymity note */}
      {unlocked && (
        <div className="mt-10 cal-card p-4 bg-[#FAFAF5] border-l-4 border-[#111]" data-testid="anonymity-note">
          <div className="flex items-start gap-3">
            <Lock size={16} className="mt-0.5" />
            <div className="text-xs text-[#333]">
              <div className="font-display font-bold mb-1">Anonymity preserved</div>
              <div>Individual rater identities are never shown. Comments are shuffled within each cohort to prevent re-identification by submission order.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LockedState({ results }) {  const gates = results?.gates || {};
  return (
    <div className="mt-6" data-testid="locked-state">
      <div className="border border-[#111] p-5 bg-[#FAFAF5]">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={16} />
          <div className="font-mono-cal text-[11px] uppercase tracking-widest text-[#111]">
            [ VAULT LOCKED · STATUS: {results?.status?.toUpperCase() || "COLLECTING"} ]
          </div>
        </div>
        <p className="text-sm text-[#333] mb-4">{results?.unlock_hint || "The vault unlocks once all gates close."}</p>
        <div className="space-y-2">
          <GateRow
            label={`Peer submissions (${gates.peers?.have || 0} / ${gates.peers?.need || 0})`}
            pass={gates.peers?.pass}
            testid="gate-peers"
          />
          <GateRow label="Manager rating submitted" pass={gates.manager?.pass} testid="gate-manager" />
          <GateRow label="Employee self-assessment submitted" pass={gates.self?.pass} testid="gate-self" />
        </div>
      </div>
    </div>
  );
}

function GateRow({ label, pass, testid }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={testid}>
      {pass ? (
        <CheckCircle2 size={16} className="text-[#00A36C]" />
      ) : (
        <AlertCircle size={16} className="text-[#B00020]" />
      )}
      <span className={pass ? "text-[#00A36C] line-through" : "text-[#B00020]"}>{label}</span>
    </div>
  );
}

function AiConsensusPanel({ synthesis, generatedAt, loading, onGenerate, perTask }) {
  // Build quick task lookup for the task-evidence section so we can render
  // the task description alongside the LLM's one-line summary.
  const taskMap = perTask.reduce((acc, t) => { acc[t.task_id] = t.description; return acc; }, {});

  if (!synthesis) {
    return (
      <section className="mb-8" data-testid="ai-consensus-cta">
        <div className="cal-card cal-shadow p-6 sm:p-8 bg-gradient-to-br from-[#FAFAF5] to-white border-l-4 border-[#FF4F00]">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-10 h-10 bg-[#FF4F00] text-white flex items-center justify-center shrink-0">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-1">[ AI PEER CONSENSUS · PHASE 6 ]</div>
              <h2 className="font-display font-bold text-2xl mb-1">Skim before you scroll</h2>
              <p className="text-sm text-[#666]">
                Let Claude read every cohort comment and surface strengths, growth areas, divergences, and task-level signal — in 60 seconds. Rater identities never enter the prompt.
              </p>
            </div>
            <button
              onClick={onGenerate}
              disabled={loading}
              className="cal-btn flex items-center gap-2"
              data-testid="ai-synthesize-btn"
            >
              {loading ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {loading ? "Synthesizing…" : "Generate consensus →"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  const stamp = generatedAt ? new Date(generatedAt).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : null;

  return (
    <section className="mb-8" data-testid="ai-consensus-panel">
      <div className="cal-card cal-shadow p-6 sm:p-8 bg-gradient-to-br from-[#FAFAF5] to-white border-l-4 border-[#FF4F00]">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FF4F00] text-white flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <div>
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ AI PEER CONSENSUS ]</div>
              <div className="font-mono-cal text-[9px] text-[#999]">
                {stamp ? `Generated ${stamp}` : ""}
              </div>
            </div>
          </div>
          <button
            onClick={onGenerate}
            disabled={loading}
            className="font-mono-cal text-[10px] uppercase tracking-widest text-[#111] underline flex items-center gap-1"
            data-testid="ai-regenerate-btn"
          >
            {loading ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {loading ? "Regenerating…" : "Regenerate"}
          </button>
        </div>

        {/* Headline */}
        {synthesis.headline && (
          <div className="mb-6" data-testid="ai-headline">
            <div className="font-display font-bold text-xl sm:text-2xl leading-snug text-[#111]">
              {synthesis.headline}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Strengths */}
          {(synthesis.strengths?.length || 0) > 0 && (
            <div data-testid="ai-strengths">
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C] mb-2">[ STRENGTHS ]</div>
              <ul className="space-y-2">
                {synthesis.strengths.map((s, i) => (
                  <li key={i} className="text-sm text-[#111] flex gap-2" data-testid={`ai-strength-${i}`}>
                    <span className="text-[#00A36C] font-bold">+</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Growth areas */}
          {(synthesis.growth_areas?.length || 0) > 0 && (
            <div data-testid="ai-growth-areas">
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#D97706] mb-2">[ GROWTH AREAS ]</div>
              <ul className="space-y-2">
                {synthesis.growth_areas.map((s, i) => (
                  <li key={i} className="text-sm text-[#111] flex gap-2" data-testid={`ai-growth-${i}`}>
                    <span className="text-[#D97706] font-bold">△</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Divergences */}
        {(synthesis.divergences?.length || 0) > 0 && (
          <div className="mt-6" data-testid="ai-divergences">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#B00020] mb-2">[ COHORT DIVERGENCES ]</div>
            <div className="space-y-2">
              {synthesis.divergences.map((d, i) => (
                <div key={i} className="border-l-2 border-[#B00020] pl-3 py-1" data-testid={`ai-divergence-${i}`}>
                  <div className="font-display font-bold text-sm text-[#111]">{d.theme}</div>
                  <div className="text-xs text-[#333]">{d.detail}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-task evidence */}
        {(synthesis.task_evidence?.length || 0) > 0 && (
          <details className="mt-6" data-testid="ai-task-evidence">
            <summary className="cursor-pointer font-mono-cal text-[10px] uppercase tracking-widest text-[#666] hover:text-[#111]">
              Per-task evidence ({synthesis.task_evidence.length})
            </summary>
            <div className="mt-3 space-y-3">
              {synthesis.task_evidence.map((t, i) => (
                <div key={i} className="border-t border-[#111]/10 pt-3" data-testid={`ai-task-evidence-${i}`}>
                  <div className="font-display font-bold text-sm text-[#111]">
                    {taskMap[t.task_id] || t.task_id}
                  </div>
                  <div className="text-sm text-[#333] mt-1">{t.summary}</div>
                  {t.cohort_signal && (
                    <div className="font-mono-cal text-[10px] text-[#666] mt-1 tracking-wider">
                      {t.cohort_signal}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}

        <div className="mt-5 pt-4 border-t border-[#111]/10 text-[11px] text-[#999]">
          AI synthesis is generated from cohort-aggregated data only. Rater identities are never shared with the model.
        </div>
      </div>
    </section>
  );
}

function ShareWithEmployeeButton({ cycle, onShared }) {
  const [sharing, setSharing] = useState(false);
  const [modal, setModal] = useState(false);
  const [result, setResult] = useState(null);

  const alreadyShared = !!cycle?.employee_share_shared_at;
  const employeeEmail = cycle?.profile_snapshot?.employee_email || "";

  const share = async () => {
    setSharing(true);
    try {
      const r = await api.post(`/review-cycles/${cycle.id}/share-with-employee`);
      setResult(r.data);
      onShared?.(r.data);
      if (r.data?.delivered) {
        toast.success("Summary sent to the employee");
      } else {
        toast.warning("Link ready — email couldn't be delivered to the employee's inbox");
      }
    } catch (err) {
      toast.error(apiError(err, "Could not share summary"));
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setModal(true)}
        className="cal-btn text-xs py-1 flex items-center gap-2"
        data-testid="share-with-employee-btn"
      >
        <Share2 size={12} />
        {alreadyShared ? "Resend to employee" : "Share with employee"}
      </button>

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="share-modal">
          <div className="bg-white max-w-lg w-full p-6 border border-[#111] cal-shadow">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[ SHARE WITH EMPLOYEE ]</div>
            <h3 className="font-display font-bold text-2xl mb-3">Send the redacted summary</h3>
            <p className="text-sm text-[#333] mb-4">
              {employeeEmail ? (
                <>We'll email <strong className="font-mono-cal text-xs">{employeeEmail}</strong> a private link to a view that shows ONLY:</>
              ) : (
                <>This profile has no employee email on file — add one before sharing.</>
              )}
            </p>
            <ul className="text-sm text-[#333] mb-5 space-y-1">
              <li>— Weighted overall score + Developing/Meeting/Exceeding band</li>
              <li>— Submission counts (peers · manager · self)</li>
              <li>— Cohort weights used in the calculation</li>
              <li>— Radar chart overlay of each cohort's view across tasks</li>
            </ul>
            <div className="border-l-4 border-[#111] bg-[#FAFAF5] p-3 mb-5 text-xs text-[#333]">
              <strong className="font-display">Not shared:</strong> written comments, AI consensus summary, per-task numeric breakdowns, individual rater identities.
            </div>

            {result?.share_url && (
              <div className="mb-4 border border-[#111] p-3 bg-[#FAFAF5]" data-testid="share-link-ready">
                <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666] mb-1">[ PRIVATE LINK ]</div>
                <div className="text-xs text-[#333] break-all font-mono-cal">{result.share_url}</div>
                <button
                  onClick={() => { navigator.clipboard.writeText(result.share_url); toast.success("Link copied"); }}
                  className="font-mono-cal text-[10px] uppercase tracking-widest text-[#111] underline mt-2"
                  data-testid="copy-share-link"
                >
                  Copy link
                </button>
              </div>
            )}

            <div className="flex justify-end gap-2 flex-wrap">
              <button
                onClick={() => { setModal(false); setResult(null); }}
                className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] underline"
                data-testid="share-cancel"
              >Close</button>
              {employeeEmail && (
                <button
                  onClick={share}
                  disabled={sharing}
                  className="cal-btn flex items-center gap-2"
                  data-testid="share-confirm"
                >
                  {sharing ? <RefreshCw size={12} className="animate-spin" /> : <Share2 size={12} />}
                  {sharing ? "Sending…" : (alreadyShared || result) ? "Resend summary" : "Send summary →"}
                </button>
              )}
            </div>
            {alreadyShared && !result && (
              <div className="mt-3 font-mono-cal text-[10px] text-[#666] uppercase tracking-widest" data-testid="share-last-sent">
                Last shared: {new Date(cycle.employee_share_shared_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
